import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Sale price <-> per-area relations, ported from `sale-auto-calc-listeners.ts` and
 * `portfolio-sale/calc-price-per-sqft-validation.ts`. Each is a raw divide/multiply by gross area;
 * the legacy UOM/currency gating (isAreaUOMCheck, isUKCurrencyCheck) and the result-UOM assignment
 * (CurrencyAreaMapping) stay in the host listener. Rounding is delegated to `deps.round`.
 *
 *   pricePerSqFt          = quotingPrice / grossArea
 *   quotingPrice          = pricePerSqFt * grossArea
 *   salePricePerArea      = salePrice    / grossArea
 *   salePrice             = salePricePerArea * grossArea
 *   currentRentPerAnnum   = currentRentPerAnnumPerArea * grossArea
 *   currentRentPerArea    = currentRentPerAnnum / grossArea
 *   portfolioPricePerSqFt = portfolioSalesPrice / portfolioNra
 */
const num = v => v == null ? null : Number(v);
function divideRule(id, numeratorKey, areaKey, outKey) {
  return {
    id,
    inputs: [numeratorKey, areaKey],
    outputs: [outKey],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      const round = deps?.round ?? defaultRound;
      const numerator = num(ctx.get(numeratorKey));
      const area = num(ctx.get(areaKey));
      ctx.set(outKey, numerator == null || !area ? null : round(numerator / area, outKey));
    }
  };
}
function multiplyRule(id, rateKey, areaKey, outKey) {
  return {
    id,
    inputs: [rateKey, areaKey],
    outputs: [outKey],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      const round = deps?.round ?? defaultRound;
      const rate = num(ctx.get(rateKey));
      const area = num(ctx.get(areaKey));
      ctx.set(outKey, rate == null || area == null ? null : round(rate * area, outKey));
    }
  };
}
export const pricePerSqFtFromQuotingRule = divideRule('sale.price-per-sqft.from-quoting-price', FieldKey.QuotingPrice, FieldKey.SaleGrossArea, FieldKey.PricePerSqFt);
export const quotingPriceFromPerSqFtRule = multiplyRule('sale.quoting-price.from-price-per-sqft', FieldKey.PricePerSqFt, FieldKey.SaleGrossArea, FieldKey.QuotingPrice);
export const salePricePerAreaFromPriceRule = divideRule('sale.price-per-area.from-sale-price', FieldKey.SalePrice, FieldKey.SaleGrossArea, FieldKey.SalePricePerArea);
export const salePriceFromPerAreaRule = multiplyRule('sale.price.from-price-per-area', FieldKey.SalePricePerArea, FieldKey.SaleGrossArea, FieldKey.SalePrice);
export const currentRentPerAnnumFromPerAreaRule = multiplyRule('sale.current-rent-per-annum.from-per-area', FieldKey.CurrentRentPerAnnumPerArea, FieldKey.SaleGrossArea, FieldKey.CurrentRentPerAnnum);
export const currentRentPerAreaFromAnnumRule = divideRule('sale.current-rent-per-area.from-annum', FieldKey.CurrentRentPerAnnum, FieldKey.SaleGrossArea, FieldKey.CurrentRentPerAnnumPerArea);
export const portfolioPricePerSqFtRule = divideRule('sale.portfolio-price-per-sqft.from-price-nra', FieldKey.PortfolioSalesPrice, FieldKey.PortfolioNra, FieldKey.PortfolioPricePerSqFt);
export const salePricePerAreaRules = [pricePerSqFtFromQuotingRule, quotingPriceFromPerSqFtRule, salePricePerAreaFromPriceRule, salePriceFromPerAreaRule, currentRentPerAnnumFromPerAreaRule, currentRentPerAreaFromAnnumRule, portfolioPricePerSqFtRule];
